import { ChartCardTitle } from '@frontend/chart/components';
import { ChartInfoKind } from '@frontend/chart/types';
import { Spinner } from '@frontend/components/common';

import { InsightCardWidth, getInsightCardWidthClass } from '../insight-card-width';
import { useInsightDetailMode } from '../insight-detail-mode.context';
import { InsightOpenDetailButton } from '../insight-open-detail-button.component';

export interface InsightCardProps {
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    insightTitle?: string | null;
    /** When set, shows a top-left open-detail button (card body is not clickable). */
    insightId?: string;
    chartKey?: string;
    infoKind?: ChartInfoKind;
    isLoading?: boolean;
    /** Grid span on the insights 4-column layout (1–4). Defaults to 2. */
    width?: InsightCardWidth;
    children: React.ReactNode;
}

export const InsightCard = (props: InsightCardProps) => {
    const isDetailMode = useInsightDetailMode();
    const widthClass = isDetailMode ? 'w-full' : getInsightCardWidthClass(props.width);
    const showOpenButton = Boolean(props.insightId) && !isDetailMode;

    const titleClassName = 'text-foreground-default justify-start text-2xl font-semibold';
    const subtitleClassName = 'text-brand-muted justify-start text-xl font-semibold';
    const insightSubtitleClassName = 'text-lg font-medium';

    const titleElement = (
        <ChartCardTitle
            title={props.insightTitle ?? props.title}
            subtitle={props.insightTitle ? props.title : props.subtitle}
            chartKey={props.chartKey}
            infoKind={props.infoKind}
            titleClassName={titleClassName}
            subtitleClassName={props.insightTitle ? insightSubtitleClassName : subtitleClassName}
        />
    );

    const cardClassName = `${widthClass} bg-background-surface outline-border-default flex min-w-0 w-full flex-col items-start justify-start gap-4 rounded-xl p-4 outline-1 -outline-offset-1 ${
        isDetailMode ? 'min-h-[70vh]' : ''
    }`;

    // Detail page already shows title in OverviewHeader — avoid duplicating the card header.
    const header = isDetailMode ? null : (
        <div className='flex w-full shrink-0 items-start justify-start gap-3'>
            {showOpenButton && props.insightId ? <InsightOpenDetailButton insightId={props.insightId} /> : null}
            <div className='flex min-w-0 flex-1 flex-col items-start justify-start gap-2'>{titleElement}</div>
        </div>
    );

    if (props.isLoading) {
        return (
            <div className={cardClassName}>
                {header}
                {!isDetailMode ? <div className='text-brand-muted justify-start text-xl font-semibold'>--</div> : null}
                <Spinner />
            </div>
        );
    }

    return (
        <div className={cardClassName}>
            {header}
            <div className={`w-full min-w-0 ${isDetailMode ? 'flex min-h-0 flex-1 flex-col' : ''}`}>{props.children}</div>
        </div>
    );
};
